import React, { useContext, useMemo } from 'react'
import FormatCurrency from 'components/Common/FormatCurrency'
import Caption from 'components/Luxkit/Typography/Caption'
import Heading from 'components/Luxkit/Typography/Heading'
import Pill from 'components/Luxkit/Pill'
import TextButton from 'components/Luxkit/Button/TextButton'
import Group from 'components/utils/Group'
import PaletteProvider from 'contexts/PaletteProvider'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import TaxesAndFees from 'components/OfferPage/PackageOption/Pricing/TaxesAndFees'
import { ValueWrapper } from 'components/Common/OfferPricing/styles'
import { getBundleValueUp } from 'lib/bundleAndSave/getBundleValueUp'
import GeoContext from 'contexts/geoContext'
import useHotelPriceColour from 'hooks/useHotelPriceColour'
import { pluralizeToString } from 'lib/pluralize'
import { sum } from 'lib/arrayUtils'

interface Props {
  offer: App.BundleOffer | App.BundleOfferSummary;
  pkg: App.BundlePackageWithPrice;
}

function HeroBundleAndSaveOfferTilePricing(props: Props) {
  const { offer, pkg } = props

  const { showTaxExclusivePrices } = useContext(GeoContext)
  const propertyFees = pkg?.propertyFees ?? 0
  const taxesAndFees = pkg.taxesAndFees || 0

  const hotelPrice = pkg.price + (pkg.surcharge ?? 0)
  const totalPrice = showTaxExclusivePrices ? hotelPrice : (hotelPrice + propertyFees)

  const { shouldShowDiscountPercent, hotelDiscountPercent, shouldShowValue, valuedUpTo } = getBundleValueUp(offer, pkg)
  const exclusivePriceColor = useHotelPriceColour(taxesAndFees)

  const duration = useMemo(() => {
    return sum(pkg.packages, (p) => p.duration)
  }, [pkg.packages])

  return (
    <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
      <Group direction="vertical">
        <Caption variant="medium" colour="neutral-three"> {pluralizeToString('night', duration)}, 2 hotels from</Caption>
        <Heading variant="heading3" colour={exclusivePriceColor}>
          <FormatCurrency value={Math.ceil(totalPrice)} />
          <Caption variant="medium" as="span" colour="neutral-two">/{offer.saleUnit}</Caption>
        </Heading>
        {shouldShowValue && (
          <ValueWrapper variant="large" weight="bold" colour="neutral-one">
            Valued up to <FormatCurrency value={Math.ceil(valuedUpTo)} />
            {shouldShowDiscountPercent &&
              <PaletteProvider type="default">
                <Pill kind="primary" variant="success">-{hotelDiscountPercent}%</Pill>
              </PaletteProvider>
            }
          </ValueWrapper>
        )}
        <TaxesAndFees
          taxesPayableAtProperty={offer.property?.taxesPayableAtProperty}
          taxAmount={pkg.taxesAndFees}
        />
      </Group>
      <CSSBreakpoint min="md">
        <TextButton kind="primary">
          View offer
        </TextButton>
      </CSSBreakpoint>
    </Group>
  )
}

export default HeroBundleAndSaveOfferTilePricing
